/**
 * SupervisorObservarPage — El supervisor/administrador se une a una teleconsulta
 * EN CURSO como OBSERVADOR (no como médico tratante).
 *
 * - Antes de entrar elige: visible (cámara+micrófono) u oculto (sin video/mic).
 * - Dentro puede activar/desactivar cámara y micrófono en cualquier momento.
 * - Puede salir cuando quiera (no afecta al médico ni al paciente).
 * - NO ve el editor de notas, ni grabación, ni "finalizar consulta": es una
 *   vista de sólo observación.
 *
 * La identidad del token es `supervisor_<uid>` (ver getLivekitToken), lo que
 * permite al cliente del médico mostrar un indicador con su nombre.
 *
 * SOLO se puede entrar a una videollamada YA INICIADA. Entrar antes no servía de
 * nada —no hay a quién observar— y tenía un efecto grave: la entrada del
 * supervisor CREABA el room en LiveKit y su salida lo dejaba vacío; al cerrarlo
 * LiveKit por inactividad, el backend lo leía como caída de la llamada y
 * devolvía al paciente a la cola "por fallo técnico". La pantalla previa espera
 * ahora a que el médico inicie el video y se habilita sola.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  LiveKitRoom,
  useTracks,
  useRemoteParticipants,
  useLocalParticipant,
  VideoTrack,
  AudioTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  Eye, EyeOff, Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, AlertTriangle,
} from 'lucide-react';
import { db, functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './SupervisorObservarPage.module.css';

interface LiveKitTokenResult {
  token: string;
  url: string;
  room: string;
  identity: string;
  esMedico: boolean;
  esSupervisor?: boolean;
  /** Política ICE resuelta por el backend desde `config/livekit.forzarRelay`. */
  iceTransportPolicy?: RTCIceTransportPolicy;
}

const getLivekitTokenFn = httpsCallable<{ consultaId: string }, LiveKitTokenResult>(
  functions,
  'portal_getLivekitToken',
);

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

/** Sincroniza cámara/micrófono locales del observador con el estado del UI. */
function LocalMediaSync({ camOn, micOn }: { camOn: boolean; micOn: boolean }) {
  const { localParticipant } = useLocalParticipant();
  useEffect(() => {
    if (!localParticipant) return;
    localParticipant.setCameraEnabled(camOn).catch(() => undefined);
  }, [localParticipant, camOn]);
  useEffect(() => {
    if (!localParticipant) return;
    localParticipant.setMicrophoneEnabled(micOn).catch(() => undefined);
  }, [localParticipant, micOn]);
  return null;
}

/** Tile de un participante remoto identificado por prefijo de identidad. */
function RemoteTile({ prefix, label }: { prefix: string; label: string }) {
  useRemoteParticipants(); // re-render on join/leave
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.Microphone, withPlaceholder: true },
    ],
    { onlySubscribed: false },
  );
  const remote = tracks.filter(
    (t) => !t.participant.isLocal && (t.participant.identity ?? '').startsWith(prefix),
  );
  const vid = remote.find((t) => t.source === Track.Source.Camera && t.publication?.track);
  const aud = remote.find((t) => t.source === Track.Source.Microphone && t.publication?.track);

  return (
    <div className={styles.tile}>
      {aud?.publication?.track && <AudioTrack trackRef={aud} volume={1} />}
      {vid?.publication?.track ? (
        <VideoTrack trackRef={vid} className={styles.tileVideo} />
      ) : (
        <div className={styles.placeholder}>
          <div className={styles.avatar}>{getInitials(label)}</div>
        </div>
      )}
      <div className={styles.tileLabel}>{label}</div>
    </div>
  );
}

export default function SupervisorObservarPage() {
  const { id: consultaId } = useParams<{ id: string }>();
  const { role } = useAuth();
  const navigate = useNavigate();

  const [joined, setJoined] = useState(false);
  const [tokenRes, setTokenRes] = useState<LiveKitTokenResult | null>(null);
  const [camOn, setCamOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esSupervisor = role === 'admin' || role === 'supervisor';

  /* Estado en vivo de la consulta: mientras la videollamada no haya iniciado no
     hay nada que observar y entrar sería contraproducente (ver cabecera). */
  const [videoActivo, setVideoActivo] = useState<boolean | null>(null);
  const [estadoConsulta, setEstadoConsulta] = useState<string | null>(null);

  useEffect(() => {
    if (!consultaId || !esSupervisor) return;
    return onSnapshot(
      doc(db, 'consultas', consultaId),
      (snap) => {
        const c = snap.data();
        setEstadoConsulta((c?.estado as string) ?? null);
        setVideoActivo(!!c?.videoInicioEn && !c?.videoFinalizado);
      },
      () => { setVideoActivo(null); },
    );
  }, [consultaId, esSupervisor]);

  const join = useCallback(
    async (startVisible: boolean) => {
      if (!consultaId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getLivekitTokenFn({ consultaId });
        setTokenRes(res.data);
        setCamOn(startVisible);
        setMicOn(startVisible);
        setJoined(true);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'No se pudo unir a la teleconsulta.');
      } finally {
        setLoading(false);
      }
    },
    [consultaId],
  );

  const salir = useCallback(() => {
    setJoined(false);
    setTokenRes(null);
    navigate(-1);
  }, [navigate]);

  const header = useMemo(
    () => (
      <div className={styles.topbar}>
        <div className={styles.brand}>
          <Eye size={18} aria-hidden="true" />
          <span>Modo supervisión</span>
        </div>
        <button type="button" className={styles.leaveBtn} onClick={salir}>
          <PhoneOff size={15} aria-hidden="true" /> Salir
        </button>
      </div>
    ),
    [salir],
  );

  if (!esSupervisor) {
    return (
      <div className={styles.centered}>
        <AlertTriangle size={20} aria-hidden="true" />
        <p>Sólo supervisores o administradores pueden observar teleconsultas.</p>
        <button type="button" className={styles.secondaryBtn} onClick={() => navigate(-1)}>Volver</button>
      </div>
    );
  }

  const consultaCerrada = estadoConsulta === 'finalizada' || estadoConsulta === 'cancelada';

  // ── Pantalla previa: elegir modo de entrada ──────────────
  if (!joined) {
    return (
      <div className={styles.prejoin}>
        <div className={styles.prejoinCard}>
          <div className={styles.brand}><Eye size={20} aria-hidden="true" /> <span>Unirse a la teleconsulta</span></div>
          <p className={styles.prejoinText}>
            Ingresará como <strong>observador</strong>. El médico verá un aviso con su nombre.
            Puede cambiar cámara y micrófono en cualquier momento.
          </p>
          {error && <div className={styles.error}><AlertTriangle size={15} /> {error}</div>}
          {videoActivo === false && (
            <div className={styles.aviso}>
              <Loader2 size={15} className={styles.spin} aria-hidden="true" />
              <span>
                {consultaCerrada
                  ? 'Esta teleconsulta ya terminó: no hay videollamada que observar.'
                  : 'La videollamada aún no ha comenzado. Se habilitará en cuanto el médico la inicie.'}
              </span>
            </div>
          )}
          <div className={styles.prejoinActions}>
            <button type="button" className={styles.primaryBtn} disabled={loading || !videoActivo} onClick={() => join(true)}>
              {loading ? <Loader2 size={16} className={styles.spin} /> : <Video size={16} />} Entrar con cámara y micrófono
            </button>
            <button type="button" className={styles.secondaryBtn} disabled={loading || !videoActivo} onClick={() => join(false)}>
              {loading ? <Loader2 size={16} className={styles.spin} /> : <EyeOff size={16} />} Entrar oculto (sin video)
            </button>
          </div>
          <button type="button" className={styles.linkBtn} onClick={() => navigate(-1)}>Cancelar</button>
        </div>
      </div>
    );
  }

  // ── Sala en observación ──────────────────────────────────
  return (
    <div className={styles.shell}>
      {header}
      {tokenRes && (
        <LiveKitRoom
          serverUrl={tokenRes.url}
          token={tokenRes.token}
          connect
          connectOptions={{
            // La política viaja en el token (config/livekit.forzarRelay). Estaba
            // fija en 'relay' aquí, de modo que esta vista seguía atada al TURN
            // aunque la videollamada ya no lo estuviera: con el relay caído el
            // supervisor no podía observar ninguna consulta. Sin valor del
            // servidor se asume 'all', que deja a ICE buscar ruta.
            rtcConfig: { iceTransportPolicy: tokenRes.iceTransportPolicy ?? 'all' },
          }}
          video={camOn}
          audio={micOn}
          onDisconnected={salir}
          onError={(e) => setError(e.message)}
          className={styles.room}
        >
          <LocalMediaSync camOn={camOn} micOn={micOn} />
          <div className={styles.grid}>
            <RemoteTile prefix="paciente_" label="Paciente" />
            <RemoteTile prefix="medico_" label="Médico" />
          </div>
          <div className={styles.controls}>
            <button
              type="button"
              className={`${styles.ctrl} ${!camOn ? styles.ctrlOff : ''}`}
              onClick={() => setCamOn((v) => !v)}
              title={camOn ? 'Apagar cámara' : 'Encender cámara'}
            >
              {camOn ? <Video size={18} /> : <VideoOff size={18} />}
            </button>
            <button
              type="button"
              className={`${styles.ctrl} ${!micOn ? styles.ctrlOff : ''}`}
              onClick={() => setMicOn((v) => !v)}
              title={micOn ? 'Silenciar micrófono' : 'Activar micrófono'}
            >
              {micOn ? <Mic size={18} /> : <MicOff size={18} />}
            </button>
            <button type="button" className={styles.ctrlLeave} onClick={salir} title="Salir de la observación">
              <PhoneOff size={18} />
            </button>
          </div>
          {!camOn && !micOn && (
            <div className={styles.hiddenBanner}>
              <EyeOff size={14} aria-hidden="true" /> Está observando de forma oculta (sin cámara ni micrófono).
            </div>
          )}
        </LiveKitRoom>
      )}
    </div>
  );
}
